import { useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { CommentOut, Severity, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { commentTarget } from "../../lib/bookmarks.ts";
import { aboutOf, commentAnchor, type DiscussionCtx, isNew, useLinkedComment } from "../../lib/conversation.ts";
import { formatInstant, formatRelative } from "../../lib/format.ts";
import { outsideOf, spaceApi, spaceUrl } from "../../lib/paths.ts";
import { displayName } from "../../lib/people.ts";
import { isReport } from "../../lib/reports.ts";
import { useAction } from "../../lib/resource.ts";
import { atLeast, canEdit } from "../../lib/roles.ts";
import { canComment, me } from "../../lib/state.ts";
import { toast } from "../../lib/toast.ts";
import { Avatar, Person } from "../../ui/Avatar.tsx";
import { BookmarkButton } from "../../ui/BookmarkButton.tsx";
import { InlineButton, Link } from "../../ui/Button.tsx";
import { Chip } from "../../ui/Chip.tsx";
import { ConfirmDialog } from "../../ui/Dialog.tsx";
import { EmojiPicker } from "../../ui/EmojiPicker.tsx";
import { Checkbox } from "../../ui/form.tsx";
import { CommentIcon, DismissedIcon, ReactIcon, ResolvedIcon } from "../../ui/icons.tsx";
import { Badge } from "../../ui/misc.tsx";
import { iconButtonClass, LINK } from "../../ui/styles.ts";
import { Attached } from "./attached.tsx";
import { Composer } from "./composer.tsx";
import { ReportHead } from "./report.tsx";
import { RichText } from "./richText.tsx";

/**
 * A thread on a place in its item's file (a document's text, a sheet's cells: docs/DOCUMENTS.md §12): what it's on,
 * quoted as it was, and a link to the file with the comment picked (its pane open on it, the text shown).
 */
function PlaceOf({ space, c }: { space: SpaceOut; c: CommentOut }) {
  const at = (c.anchor?.at ?? {}) as Record<string, unknown>;
  const range = typeof at["range"] === "string" ? at["range"] : null;
  const quote = typeof at["quote"] === "string" ? at["quote"] : range;
  return (
    <div class="flex flex-col items-start gap-1">
      {quote ? <p class="line-clamp-3 border-l-2 border-violet-300 pl-2 text-zinc-600">{quote}</p> : null}
      {c.item ? (
        <Link href={spaceUrl(space, `/items/${c.item}?note=c:${c.id}`)} class={`${LINK} text-xs`}>
          {t("notes.open")}
        </Link>
      ) : null}
    </div>
  );
}

/** What a closing or re-grading did, said after its author's name. */
function EventText({ c }: { c: CommentOut }) {
  const e = c.event;
  if (!e) {
    return null;
  }
  if (e === "reopened") {
    return <span class="text-zinc-600">{t("comments.event.reopened")}</span>;
  }
  if (e === "resolved" || e === "dismissed") {
    return (
      <span class="inline-flex items-center gap-1 text-zinc-600">
        {e === "resolved" ? <ResolvedIcon size={14} /> : <DismissedIcon size={14} />}
        {t(`comments.event.${e}`)}
      </span>
    );
  }
  const sev = (s: Severity) => t(`severity.${s}`);
  return (
    <span class="text-zinc-600">
      {t("comments.event.adjusted", { from: sev(e.adjusted.from), to: sev(e.adjusted.to) })}
    </span>
  );
}

/**
 * Deleting a comment: it can be brought back from History; admins may also erase it from there, every version of
 * it (something that shouldn't have been written), for good.
 */
function DeleteComment({
  ctx,
  c,
  open,
  onClose,
}: {
  ctx: DiscussionCtx;
  c: CommentOut;
  open: boolean;
  onClose: () => void;
}) {
  const { space } = ctx;
  const [purge, setPurge] = useState(false);
  const [remove] = useAction(async () => {
    await api.del(spaceApi(space, `/comments/${c.id}`), purge ? { query: { purge: true } } : {});
    toast(t(purge ? "comments.purged" : "comments.deleted"));
    ctx.reload();
  });
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t("comments.deleteTitle")}
      message={
        <div class="flex flex-col gap-3">
          <p>{purge ? t("comments.purgeMessage") : t("comments.deleteMessage")}</p>
          {atLeast(space.role, "admin") ? (
            <Checkbox checked={purge} onCheck={setPurge} label={t("comments.purge")} />
          ) : null}
        </div>
      }
      confirmLabel={purge ? t("comments.purgeConfirm") : t("common.delete")}
      onConfirm={() => remove()}
    />
  );
}

/** Adds or takes back your reaction to `c`, then refreshes. */
function useReact(ctx: DiscussionCtx, c: CommentOut) {
  const [run] = useAction(async (emoji: string, on: boolean) => {
    await api.post(spaceApi(ctx.space, `/comments/${c.id}/reactions`), { emoji, on });
    ctx.reload();
  });
  return run;
}

/** Opens the emoji picker to react to a comment. */
function AddReaction({ ctx, c }: { ctx: DiscussionCtx; c: CommentOut }) {
  const react = useReact(ctx, c);
  // Reacting goes with commenting (from outside too).
  if (!canComment(ctx.space)) {
    return null;
  }
  return (
    <EmojiPicker label={t("reactions.add")} onPick={(u) => react(u, true)} class={iconButtonClass("2xs")}>
      <ReactIcon size={15} />
    </EmojiPicker>
  );
}

/** A comment's reactions: each emoji with how many (yours highlighted, who on hover); clicking adds or takes back yours. */
function Reactions({ ctx, c }: { ctx: DiscussionCtx; c: CommentOut }) {
  const react = useReact(ctx, c);
  const list = c.reactions ?? [];
  if (list.length === 0) {
    return null;
  }
  const can = canComment(ctx.space);
  return (
    <p class="mt-1 flex flex-wrap gap-1.5">
      {list.map((r) => (
        <Chip
          key={r.emoji}
          size="md"
          tone={r.mine ? "blue" : "gray"}
          disabled={!can}
          pressed={r.mine}
          title={r.names.join(", ")}
          onClick={() => react(r.emoji, !r.mine)}
          icon={<span class="text-sm leading-none">{r.emoji}</span>}
        >
          {r.count}
        </Chip>
      ))}
    </p>
  );
}

/** A comment as a social media post: avatar beside it, actions below. */
interface PostLook {
  avatar: number;
  /** How many replies it has (said in its actions). */
  replies?: number;
}

/** When it was written and edited (exactly, on hover); in a post, each after a dot. */
function When({ c, post }: { c: CommentOut; post: boolean }) {
  const dot = post ? "· " : "";
  return (
    <>
      <span class="text-xs text-zinc-500" title={formatInstant(c.created_at)}>
        {`${dot}${formatRelative(c.created_at)}`}
      </span>
      {c.edited_at ? (
        <span class="text-xs text-zinc-400" title={formatInstant(c.edited_at)}>
          {`${dot}${t("comments.edited")}`}
        </span>
      ) : null}
    </>
  );
}

/**
 * What can be done to a comment: reply, react, bookmark, edit or delete it. Its author may edit or delete it; admins
 * delete it too; outside the space, your own only (taking it back even once it takes no more comments; no bookmarks).
 */
function CommentActions({
  ctx,
  c,
  post,
  onReply,
  onEdit,
  onDelete,
}: {
  ctx: DiscussionCtx;
  c: CommentOut;
  post: PostLook | undefined;
  onReply: (() => void) | undefined;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { space } = ctx;
  const mine = c.author === me.value?.id;
  const outside = outsideOf(space) !== undefined;
  const deletes = outside ? mine : canEdit(space) && (mine || atLeast(space.role, "admin"));
  const edits = mine && !c.event && (outside ? canComment(space) : canEdit(space));
  const reply =
    onReply && canComment(space) ? (
      post ? (
        <InlineButton size="xs" icon={<CommentIcon size={14} />} onClick={onReply}>
          {post.replies ? t("comments.repliesCount", { count: post.replies }) : t("comments.reply")}
        </InlineButton>
      ) : (
        <InlineButton size="xs" onClick={onReply}>
          {t("comments.reply")}
        </InlineButton>
      )
    ) : null;
  const react = <AddReaction ctx={ctx} c={c} />;
  // A post's reply first (how many there are); otherwise reacting first.
  const [first, second] = post ? [reply, react] : [react, reply];
  return (
    <>
      {first}
      {second}
      <BookmarkButton space={space} target={commentTarget(c.id)} size="2xs" />
      {edits ? (
        <InlineButton size="xs" onClick={onEdit}>
          {t("comments.edit")}
        </InlineButton>
      ) : null}
      {deletes ? (
        <InlineButton size="xs" tone="danger" onClick={onDelete}>
          {t("common.delete")}
        </InlineButton>
      ) : null}
    </>
  );
}

/** A comment: who, when, what (a closing or re-grading too), its reactions, and what can be done to it. */
export function CommentView({
  ctx,
  c,
  onReply,
  post,
  place = true,
}: {
  ctx: DiscussionCtx;
  c: CommentOut;
  onReply?: (() => void) | undefined;
  post?: PostLook | undefined;
  /** A thread on a place in a file shows what it's on (not beside the file: the review pane's). */
  place?: boolean;
}) {
  const { space } = ctx;
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const fresh = isNew(c, ctx.seen, me.value?.id);
  const body = editing ? (
    <Composer
      space={space}
      about={aboutOf(ctx)}
      placeholder={t("comments.placeholder")}
      sendLabel={t("common.save")}
      initial={c.body ?? ""}
      autoFocus={true}
      onCancel={() => setEditing(false)}
      send={async (text) => {
        await api.patch(spaceApi(space, `/comments/${c.id}`), { body: text });
        setEditing(false);
        ctx.reload();
      }}
    />
  ) : c.body ? (
    <RichText space={space} text={c.body} />
  ) : null;
  // On a place in its item's file: the text it's on, and the way there (unless shown beside it already). A report: its
  // label and period.
  const where =
    c.anchor && place ? <PlaceOf space={space} c={c} /> : isReport(c) ? <ReportHead space={space} c={c} /> : null;
  const actions = (
    <CommentActions
      ctx={ctx}
      c={c}
      post={post}
      onReply={onReply}
      onEdit={() => setEditing(true)}
      onDelete={() => setConfirm(true)}
    />
  );
  const remove = <DeleteComment ctx={ctx} c={c} open={confirm} onClose={() => setConfirm(false)} />;
  // Linked to (a bookmark's link): scrolled to, and marked while the address says so.
  const linked = useLinkedComment(c.id);
  const mark = linked ? "rounded-md bg-amber-50 ring-2 ring-amber-300 ring-offset-4 ring-offset-amber-50" : "";
  if (post) {
    return (
      <div id={commentAnchor(c.id)} class={`flex gap-3 text-sm ${mark}`}>
        <Avatar id={c.author} name={c.author_name} kind={c.author_kind} size={post.avatar} />
        <div class="flex min-w-0 flex-1 flex-col gap-1">
          <p class="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
            <span class={`font-semibold ${c.member ? "text-zinc-900" : "text-zinc-500"}`}>
              {displayName(c.author, c.author_name)}
            </span>
            <EventText c={c} />
            <When c={c} post={true} />
            {fresh ? (
              <span class="ml-1 self-center">
                <Badge tone="blue">{t("comments.new")}</Badge>
              </span>
            ) : null}
          </p>
          {where}
          {body}
          <Attached space={space} list={c.attachments} />
          <Reactions ctx={ctx} c={c} />
          <p class="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">{actions}</p>
        </div>
        {remove}
      </div>
    );
  }
  return (
    <div
      id={commentAnchor(c.id)}
      class={`flex flex-col gap-1 text-sm ${fresh ? "border-l-2 border-blue-500 pl-2" : ""} ${mark}`}
    >
      <p class="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span class={`font-medium ${c.member ? "text-zinc-900" : "text-zinc-500"}`}>
          <Person id={c.author} name={c.author_name} kind={c.author_kind} />
        </span>
        <EventText c={c} />
        <When c={c} post={false} />
        {fresh ? <Badge tone="blue">{t("comments.new")}</Badge> : null}
        <span class="ml-auto flex items-center gap-2 text-xs">{actions}</span>
      </p>
      {where}
      {body}
      <Attached space={space} list={c.attachments} />
      <Reactions ctx={ctx} c={c} />
      {remove}
    </div>
  );
}
